import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiService } from '../api/api.service';
import {
  RetrievalLookupInput,
  RetrievalOrderDetail,
  RetrievalOrdersResponse,
  RetrievalSession,
  RetrievalSubjectKind,
} from '../../models/ticket-retrieval.model';

/**
 * Client for the Find My Tickets flow.
 *
 * The retrieval session is held in `sessionStorage` and sent on a dedicated
 * `X-Ticket-Retrieval-Token` header — never `Authorization`. That keeps it
 * clear of the app's auth interceptor (which attaches the signed-in user's
 * bearer token and runs a refresh/logout cycle on 401), so a customer who
 * happens to also be logged in can use ticket recovery without disturbing
 * their account session, and vice versa.
 *
 * sessionStorage rather than localStorage: the grant should die with the tab.
 * It survives a refresh (so the customer can reload the order list), but does
 * not linger on a shared machine after the window is closed.
 */
@Injectable({ providedIn: 'root' })
export class TicketRetrievalService {
  private static readonly SESSION_KEY = 'hl_ticket_retrieval_session';
  private static readonly HEADER = 'X-Ticket-Retrieval-Token';

  private api: ApiService = inject(ApiService);
  private platformId = inject(PLATFORM_ID);

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Step 1 — always resolves the same way whether or not orders exist.
   *
   * The customer identifies themselves by email, by phone, or by order
   * number plus one of those. An order number alone is not accepted: they
   * are printed on receipts and forwarded in confirmation emails, so on its
   * own it would make every receipt a door key.
   */
  requestAccess(input: RetrievalLookupInput): Observable<unknown> {
    // Only send the fields that were filled in — an empty string would fail
    // the backend's email/phone validators rather than being ignored.
    const body: Record<string, string> = {};
    if (input.email?.trim()) body['email'] = input.email.trim();
    if (input.phone?.trim()) body['phone'] = input.phone.trim();
    if (input.orderNumber?.trim())
      body['orderNumber'] = input.orderNumber.trim();

    return this.api.post('ticket-retrieval/request', body);
  }

  /** Step 2 — exchange the emailed token for a session and store it. */
  verifyToken(token: string): Observable<RetrievalSession> {
    return this.api
      .post<{ data: RetrievalSession }>('ticket-retrieval/verify', { token })
      .pipe(
        map((response) => {
          const session = (response as any)?.data ?? response;
          this.storeSession(session);
          return session as RetrievalSession;
        }),
      );
  }

  listOrders(): Observable<RetrievalOrdersResponse> {
    return this.api
      .get<{ data: RetrievalOrdersResponse }>(
        'ticket-retrieval/orders',
        undefined,
        this.authHeaders(),
      )
      .pipe(map((r) => (r as any)?.data ?? r));
  }

  getOrder(orderId: string): Observable<RetrievalOrderDetail> {
    return this.api
      .get<{ data: RetrievalOrderDetail }>(
        `ticket-retrieval/orders/${orderId}`,
        undefined,
        this.authHeaders(),
      )
      .pipe(map((r) => (r as any)?.data ?? r));
  }

  resendConfirmation(
    orderId: string,
  ): Observable<{ sent: boolean; message: string }> {
    return this.api
      .post<{ data: { sent: boolean; message: string } }>(
        `ticket-retrieval/orders/${orderId}/resend`,
        {},
        this.authHeaders(),
      )
      .pipe(map((r) => (r as any)?.data ?? r));
  }

  // ---------------------------------------------------------------------------
  // Session handling
  // ---------------------------------------------------------------------------

  getSession(): RetrievalSession | null {
    if (!this.isBrowser) return null;
    const raw = sessionStorage.getItem(TicketRetrievalService.SESSION_KEY);
    if (!raw) return null;

    try {
      const session = JSON.parse(raw) as RetrievalSession;
      // Client-side expiry check is a UX nicety — it sends the customer back
      // to the email form instead of showing a 401. The server independently
      // rejects the token, which is what actually enforces the lifetime.
      if (new Date(session.expiresAt).getTime() <= Date.now()) {
        this.clearSession();
        return null;
      }
      return session;
    } catch {
      this.clearSession();
      return null;
    }
  }

  /**
   * Which contact the session is scoped to. The value itself stays inside
   * the signed session token — the client only needs to know whether it is
   * showing "orders for your email" or "orders for your phone".
   */
  get verifiedSubjectKind(): RetrievalSubjectKind | null {
    return this.getSession()?.subjectKind ?? null;
  }

  hasSession(): boolean {
    return this.getSession() !== null;
  }

  clearSession(): void {
    if (!this.isBrowser) return;
    sessionStorage.removeItem(TicketRetrievalService.SESSION_KEY);
  }

  private storeSession(session: RetrievalSession): void {
    if (!this.isBrowser || !session?.sessionToken) return;
    sessionStorage.setItem(
      TicketRetrievalService.SESSION_KEY,
      JSON.stringify(session),
    );
  }

  private authHeaders(): HttpHeaders {
    const token = this.getSession()?.sessionToken ?? '';
    return new HttpHeaders({ [TicketRetrievalService.HEADER]: token });
  }
}
